import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, apiError } from "@/lib/api";
import { formatHour, formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { CheckCircle2, Copy, Zap, Clock, Timer } from "lucide-react";

/**
 * Public booking page. Each hourly service exposes a `booking_mode`:
 *   - "hourly"       — user picks any free hour on the resource grid (default)
 *   - "fixed_slots"  — user picks from admin-defined preset slots (e.g. "Morning 6-8 AM")
 *   - "overs"        — user picks a number of overs; auto-converted to hours via units_per_hour
 */
export default function Booking() {
  const [params] = useSearchParams();
  const [services, setServices] = useState([]);
  const [slug, setSlug] = useState(params.get("service") || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [availability, setAvailability] = useState(null);
  const [selected, setSelected] = useState(null); // { resource, hour, duration_hours? }
  const [durationHours, setDurationHours] = useState(1);
  const [overs, setOvers] = useState(6);
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [method, setMethod] = useState("upi");
  const [linkedNet, setLinkedNet] = useState("Net 1");
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState("full"); // "full" | "partial"
  const [orgCfg, setOrgCfg] = useState({ booking_partial_enabled: true, booking_deposit_percent: 25, gst_rate: 18, company_type: "company" });
  const [ballType, setBallType] = useState(null);   // for bowling machine
  const [ballOvers, setBallOvers] = useState(null); // selected overs tier
  const [activePlan, setActivePlan] = useState(null); // { id, plan_name, facility_slug, hours_remaining, uses_remaining, ... }

  // If URL has ?plan=<id>, preload the plan and lock the facility to plan.facility_slug.
  useEffect(() => {
    const planId = params.get("plan");
    if (!planId) return;
    api.get("/member/memberships")
      .then((r) => {
        const p = (r.data || []).find((m) => m.id === planId);
        if (p && p.is_active) {
          setActivePlan(p);
          if (p.facility_slug) setSlug(p.facility_slug);
        } else if (p) {
          toast.error("That plan is expired — pick another or renew");
        }
      })
      .catch(() => { /* not signed in — public booking still works */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load org config (deposit %, GST, company type) once
  useEffect(() => {
    api.get("/public/ngo").then((r) => setOrgCfg((prev) => ({ ...prev, ...(r.data || {}) }))).catch(() => {});
  }, []);

  // Load service catalog. Default slug becomes the first hourly service if none picked yet.
  useEffect(() => {
    api.get("/services").then((r) => {
      // Exclude facilities currently under scheduled maintenance from the picker
      setServices((r.data || []).filter((s) => !s._downtime));
      if (!slug || !r.data.find((s) => s.slug === slug && s.type === "hourly")) {
        const first = r.data.find((s) => s.type === "hourly");
        if (first) setSlug(first.slug);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const svc = services.find((s) => s.slug === slug);
  const mode = availability?.service?.booking_mode || svc?.booking_mode || "hourly";
  const fixedSlots = availability?.service?.fixed_slots || svc?.fixed_slots || [];
  const oversPerHour = Math.max(1, availability?.service?.units_per_hour || svc?.units_per_hour || 12);
  const bookableSvcs = services.filter((s) => s.type === "hourly");
  const isBowlingMachine = slug === "bowling-machine";
  const ballPricing = availability?.service?.ball_pricing || svc?.ball_pricing || [];
  const selectedBall = ballPricing.find((b) => b.slug === ballType);
  const selectedTier = selectedBall?.tiers?.find((t) => Number(t.overs) === Number(ballOvers));

  useEffect(() => {
    if (!svc) return;
    setSelected(null);
    api.get("/availability", { params: { service_slug: slug, date } })
      .then((r) => setAvailability(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [slug, date, svc]);

  // Compute effective duration based on mode
  const effectiveDuration = useMemo(() => {
    if (mode === "fixed_slots" && selected?.duration_hours) return selected.duration_hours;
    if (isBowlingMachine && selectedTier) {
      return Math.max(1, Math.ceil(Number(selectedTier.overs) / oversPerHour));
    }
    if (mode === "overs") return Math.max(1, Math.ceil(overs / oversPerHour));
    return durationHours;
  }, [mode, selected, overs, oversPerHour, durationHours, isBowlingMachine, selectedTier]);

  const subtotal = useMemo(() => {
    if (!svc) return 0;
    // Bowling matrix: use the selected (ball type × overs) tier price
    if (isBowlingMachine && selectedTier) return Math.round(Number(selectedTier.price) || 0);
    // Prefer the date-aware price returned by /api/availability (weekend / peak / date rules).
    // Fallback to base effective_hourly_rate if the endpoint hasn't loaded yet.
    const dateRate = availability?.date_default_price;
    // If a specific slot is selected, honour the price of that hour precisely
    const perHourFromMap = selected?.hour != null
      ? (availability?.price_map?.[String(selected.hour)]?.price_per_hour)
      : undefined;
    const hourlyRate = perHourFromMap || dateRate || svc.effective_hourly_rate || (svc.unit_price || 0) * (svc.units_per_hour || 1);
    if (mode === "overs") {
      const perOver = svc.unit_price || hourlyRate / oversPerHour;
      return Math.round(perOver * overs);
    }
    return Math.round(hourlyRate * effectiveDuration);
  }, [svc, mode, overs, oversPerHour, effectiveDuration, isBowlingMachine, selectedTier, availability, selected]);

  const isCompany = (orgCfg.company_type || "company") === "company";
  const gstRate = isCompany ? Number(orgCfg.gst_rate ?? 18) : 0;
  const gstInclusive = !!orgCfg.gst_inclusive;
  // Two modes:
  //   inclusive = true  → price the guest sees ALREADY has GST baked in.
  //                        base = price / (1 + rate/100), gst = price − base, total = price.
  //   inclusive = false → price is base; GST is added on top.
  const gstAmount = gstRate === 0
    ? 0
    : gstInclusive
      ? Math.round(subtotal - subtotal / (1 + gstRate / 100))
      : Math.round((subtotal * gstRate) / 100);
  const baseExGst = gstInclusive ? subtotal - gstAmount : subtotal;
  const totalPrice = gstInclusive ? subtotal : subtotal + gstAmount;

  const depositPct = Number(orgCfg.booking_deposit_percent ?? 25);
  const partialAllowed = orgCfg.booking_partial_enabled !== false;
  const depositAmount = Math.round((totalPrice * depositPct) / 100);
  const balanceDue = Math.max(0, totalPrice - depositAmount);
  const payNow = paymentMode === "partial" && partialAllowed ? depositAmount : totalPrice;

  const submit = async () => {
    if (!selected) return toast.error("Pick a slot first");
    if (isBowlingMachine && !selectedTier) {
      return toast.error("Pick a ball type and overs first");
    }
    setLoading(true);
    try {
      const payload = {
        service_slug: slug,
        resource: selected.resource,
        date,
        start_hour: selected.hour,
        duration: effectiveDuration,
        guest_name: guestName || undefined,
        guest_phone: guestPhone || undefined,
        payment_method: method,
        payment_mode: partialAllowed ? paymentMode : "full",
      };
      if (svc?.requires_net) payload.linked_net = linkedNet;
      if (mode === "overs") payload.overs_booked = overs;
      if (isBowlingMachine && selectedTier) {
        payload.ball_type = ballType;
        payload.overs = Number(selectedTier.overs);
      }
      // If a membership plan is active, reserve against the plan (deducts hours/uses)
      // instead of creating a paid booking.
      if (activePlan) {
        const { data } = await api.post("/member/reserve", {
          membership_id: activePlan.id,
          service_slug: slug,
          resource: selected.resource,
          date,
          start_hour: selected.hour,
          duration: effectiveDuration,
        });
        setConfirmation({ ...data, used_plan: activePlan.plan_name || activePlan.facility_name });
        toast.success(`Reserved with your ${activePlan.plan_name || "plan"}`);
        const av = await api.get("/availability", { params: { service_slug: slug, date } });
        setAvailability(av.data);
        setSelected(null);
        return;
      }
      const { data } = await api.post("/bookings", payload);
      setConfirmation(data);
      toast.success("Booking confirmed");
      const av = await api.get("/availability", { params: { service_slug: slug, date } });
      setAvailability(av.data);
      setSelected(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-16" data-testid="booking-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// Live Booking</div>
      <h1 className="font-display text-5xl md:text-6xl font-black uppercase leading-none tracking-tighter">
        Book your <span className="brand-blue">slot</span>.
      </h1>
      <p className="text-white/60 mt-6 max-w-2xl">
        Different services offer different booking styles — a bowling machine is billed in <span className="brand-blue">overs</span>,
        a cricket ground in <span className="brand-blue">fixed sessions</span>, an indoor box <span className="brand-blue">per hour</span>.
        Pick your service to see the right flow.
      </p>

      {/* Active plan banner — shown when arriving from Member Portal via ?plan=<id> */}
      {activePlan && (
        <div className="mt-8 border-2 border-brand-blue bg-brand-blue/10 p-5 flex flex-wrap items-center justify-between gap-3" data-testid="booking-with-plan-banner">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">// Booking with your membership</div>
            <div className="font-display text-xl font-black uppercase mt-0.5">{activePlan.plan_snapshot?.name || activePlan.plan_name || activePlan.facility_name}</div>
            <div className="text-[11px] text-white/70 mt-1">
              {activePlan.hours_remaining != null && <>Hours left: <b className="brand-blue">{activePlan.hours_remaining}</b>. </>}
              {activePlan.uses_remaining != null && <>Uses left: <b className="brand-blue">{activePlan.uses_remaining}</b>. </>}
              This booking will deduct from your plan — no payment required.
            </div>
          </div>
          <button onClick={() => { setActivePlan(null); }} data-testid="booking-plan-clear"
            className="text-[10px] font-mono uppercase tracking-widest border border-white/25 px-3 py-1.5 hover:bg-white/10">
            Pay cash instead
          </button>
        </div>
      )}

      {/* Service + Date row */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-white/40">Service</label>
          <select
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            data-testid="booking-service"
            className="mt-2 w-full bg-[#131A2B] border border-white/12 py-3 px-4 text-white focus:outline-none focus:border-brand-blue"
          >
            {bookableSvcs.length === 0 && <option value="">Loading services…</option>}
            {bookableSvcs.map((s) => (
              <option key={s.slug} value={s.slug}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-white/40">Date</label>
          <input
            type="date" value={date}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
            data-testid="booking-date"
            className="mt-2 w-full bg-[#131A2B] border border-white/12 py-3 px-4 text-white focus:outline-none focus:border-brand-blue"
          />
        </div>
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-white/40">Booking mode</label>
          <div className="mt-2 py-3 px-4 border border-white/12 bg-[#131A2B] font-mono text-sm flex items-center gap-2" data-testid="booking-mode-badge">
            {mode === "overs" && <><Zap size={13} className="brand-orange" /> Per Overs</>}
            {mode === "fixed_slots" && <><Clock size={13} className="brand-orange" /> Fixed Slots</>}
            {mode === "hourly" && <><Timer size={13} className="brand-orange" /> Per Hour</>}
          </div>
        </div>
      </div>

      {/* Bowling Machine matrix picker (Ball type × Overs tier) */}
      {isBowlingMachine && ballPricing.length > 0 && (
        <div className="mt-6 border border-brand-orange/30 bg-brand-orange/5 p-6" data-testid="bowling-matrix-panel">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-3">
            Bowling machine · pick ball type & overs
          </div>
          {/* Ball type buttons */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {ballPricing.map((b) => (
              <button
                key={b.slug}
                type="button"
                onClick={() => { setBallType(b.slug); setBallOvers(null); }}
                data-testid={`ball-type-${b.slug}`}
                className={`text-left p-4 border transition ${
                  ballType === b.slug ? "border-brand-orange bg-brand-orange/15" : "border-white/12 hover:border-brand-blue"
                }`}
              >
                <div className="font-display text-lg font-black uppercase">{b.name}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mt-1">
                  from {formatINR(Math.min(...(b.tiers || []).map((t) => Number(t.price) || 0)) || 0)}
                </div>
              </button>
            ))}
          </div>
          {/* Overs tier chips for the selected ball */}
          {selectedBall && (
            <div className="mt-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Overs — pick one</div>
              <div className="flex flex-wrap gap-2" data-testid="overs-tier-chips">
                {(selectedBall.tiers || []).map((t) => {
                  const active = Number(ballOvers) === Number(t.overs);
                  return (
                    <button
                      key={t.overs}
                      type="button"
                      onClick={() => setBallOvers(Number(t.overs))}
                      data-testid={`overs-tier-${t.overs}`}
                      className={`px-4 py-3 border font-mono text-xs transition ${
                        active ? "border-brand-orange bg-brand-orange/15 brand-orange" : "border-white/15 hover:border-brand-blue"
                      }`}
                    >
                      <span className="font-display text-lg font-black">{t.overs}</span>
                      <span className="uppercase tracking-widest text-white/50 ml-2">overs · {formatINR(t.price)}</span>
                    </button>
                  );
                })}
              </div>
              {selectedTier && (
                <div className="mt-3 text-xs text-white/60 font-mono" data-testid="bowling-tier-summary">
                  {selectedBall.name} · {selectedTier.overs} overs · {formatINR(selectedTier.price)} · slot ≈ {effectiveDuration}h ({Math.round((selectedTier.overs / oversPerHour) * 60)} min)
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mode-specific controls */}
      {mode === "overs" && !isBowlingMachine && svc && (
        <div className="mt-6 border border-brand-orange/30 bg-brand-orange/5 p-6" data-testid="overs-panel">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-3">Bowling machine · {oversPerHour} overs per hour</div>
          <div className="flex flex-wrap items-end gap-6">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-white/50">Number of overs</label>
              <div className="flex items-center gap-2 mt-2">
                <button onClick={() => setOvers(Math.max(1, overs - 1))} data-testid="overs-dec" className="w-10 h-10 border border-white/20 hover:border-brand-orange">−</button>
                <input
                  type="number" min="1" max="60" value={overs}
                  onChange={(e) => setOvers(Math.max(1, parseInt(e.target.value) || 1))}
                  data-testid="overs-input"
                  className="w-24 text-center bg-transparent border border-white/20 py-2 px-2 font-display text-2xl font-black"
                />
                <button onClick={() => setOvers(overs + 1)} data-testid="overs-inc" className="w-10 h-10 border border-white/20 hover:border-brand-orange">+</button>
              </div>
            </div>
            <div className="text-sm text-white/70">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Equivalent slot</div>
              <div className="font-display text-2xl brand-blue mt-1" data-testid="overs-duration">{effectiveDuration}h</div>
              <div className="text-xs text-white/50">({overs} overs ≈ {(overs / oversPerHour * 60).toFixed(0)} min)</div>
            </div>
            <div className="text-sm text-white/70">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Cost</div>
              <div className="font-display text-2xl brand-blue mt-1">{formatINR(totalPrice)}</div>
              <div className="text-[10px] font-mono text-white/40">{gstRate > 0 ? `Incl. GST ${gstRate}%` : "GST included"}</div>
            </div>
          </div>
          <p className="mt-4 text-xs text-white/50">Now pick which machine + start-hour from the grid below. The booking will lock the paired indoor net for {effectiveDuration} hour(s) automatically.</p>
        </div>
      )}

      {mode === "hourly" && svc && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-white/40">Duration (hrs)</label>
            <select
              value={durationHours}
              onChange={(e) => setDurationHours(parseInt(e.target.value))}
              data-testid="booking-duration"
              className="mt-2 w-full bg-[#131A2B] border border-white/12 py-3 px-4 text-white"
            >
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-white/40">Rate</label>
            <div className="mt-2 py-3 px-4 border border-white/12 bg-[#131A2B] font-mono brand-blue" data-testid="booking-rate">
              {isBowlingMachine
                ? (selectedTier
                    ? `${formatINR(selectedTier.price)} · ${selectedTier.overs} overs`
                    : "Pick ball type & overs →")
                : <>{formatINR(availability?.date_default_price || svc.effective_hourly_rate || svc.unit_price || 0)}/hr</>}
              {!isBowlingMachine && availability?.date_rule_applied && (
                <span className="ml-2 text-[10px] uppercase tracking-widest brand-orange">
                  · {availability.date_rule_applied.replace("_", " ")} rate
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {mode === "fixed_slots" && svc && (
        <div className="mt-6" data-testid="fixed-slots-panel">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-3">Pick a pre-defined session</div>
          {fixedSlots.length === 0 ? (
            <div className="border border-white/12 bg-[#131A2B] p-6 text-white/50 text-sm">
              No pre-defined slots yet. Admin can add them under <span className="brand-blue">Admin · Service Config</span>.
              Meanwhile, please contact the reception to book this service.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {fixedSlots.map((fs, i) => {
                const isSelected = selected?.hour === fs.start_hour && selected?.duration_hours === fs.duration_hours;
                return (
                  <button
                    key={`${fs.start_hour}-${fs.duration_hours}-${i}`}
                    onClick={() => setSelected((prev) => ({ ...prev, hour: fs.start_hour, duration_hours: fs.duration_hours }))}
                    data-testid={`fixed-slot-${i}`}
                    className={`text-left p-4 border transition ${isSelected ? "border-brand-orange bg-brand-orange/10" : "border-white/12 hover:border-brand-blue"}`}
                  >
                    <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{fs.label || `Session ${i + 1}`}</div>
                    <div className="font-display text-xl font-black mt-1">
                      {formatHour(fs.start_hour)}<span className="text-white/40 mx-1">→</span>{formatHour(fs.start_hour + fs.duration_hours)}
                    </div>
                    <div className="text-xs text-white/50 mt-1">{fs.duration_hours}h · {formatINR((availability?.date_default_price || svc.effective_hourly_rate || svc.unit_price || 0) * fs.duration_hours)}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {svc?.requires_net && (
        <div className="mt-6 border border-white/12 bg-[#131A2B] p-6">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-2">Bowling machine dependency</div>
          <div className="text-sm text-white/60 mb-3">This machine must be paired with an available indoor net for the same time window.</div>
          <select
            value={linkedNet}
            onChange={(e) => setLinkedNet(e.target.value)}
            data-testid="booking-linked-net"
            className="bg-[#0B1120] border border-white/12 py-2 px-4 text-white"
          >
            {["Net 1", "Net 2", "Net 3", "Net 4"].map((n) => <option key={n}>{n}</option>)}
          </select>
        </div>
      )}

      {/* Availability grid — always shown; user picks the machine/court + start hour here */}
      {availability && (
        <div className="mt-10 overflow-x-auto border border-white/12" data-testid="availability-matrix">
          <table className="w-full font-mono text-xs">
            <thead>
              <tr className="bg-[#131A2B]">
                <th className="p-3 text-left text-white/40 uppercase tracking-widest w-32">Resource</th>
                {availability.hours.map((h) => (
                  <th key={h} className="p-2 text-center text-white/40">{formatHour(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(availability.matrix).map(([resource, hours]) => (
                <tr key={resource} className="border-t border-white/12">
                  <td className="p-3 uppercase tracking-widest font-bold">{resource}</td>
                  {availability.hours.map((h) => {
                    const booked = hours[h];
                    // Fixed-slot mode: only enable the start-hour cells that match a pre-defined slot
                    const isFixedStart = mode === "fixed_slots" ? fixedSlots.some((fs) => fs.start_hour === h) : true;
                    const isSelected = selected?.resource === resource && h >= selected?.hour && h < (selected?.hour || 0) + effectiveDuration;
                    const disabled = booked || !isFixedStart;
                    return (
                      <td
                        key={h}
                        data-testid={`slot-${resource}-${h}`}
                        onClick={() => {
                          if (disabled) return;
                          if (mode === "fixed_slots") {
                            const fs = fixedSlots.find((x) => x.start_hour === h);
                            if (fs) setSelected({ resource, hour: fs.start_hour, duration_hours: fs.duration_hours });
                          } else {
                            setSelected({ resource, hour: h });
                          }
                        }}
                        className={`matrix-cell text-center cursor-pointer text-[10px] ${
                          booked ? "booked cursor-not-allowed" :
                          !isFixedStart ? "opacity-30 cursor-not-allowed" :
                          isSelected ? "selected" : "free"
                        }`}
                      >
                        {booked ? "✕" : isSelected ? "▶" : "·"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Guest details + submit */}
      {selected && (
        <div className="mt-10 border border-white/12 bg-[#131A2B] p-6 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="booking-summary">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">Selection</div>
            <div className="font-display text-2xl font-black brand-blue">{selected.resource}</div>
            <div className="text-xs text-white/60 mt-1 font-mono">
              {formatHour(selected.hour)} → {formatHour(selected.hour + effectiveDuration)} · {effectiveDuration}h
              {mode === "overs" && <> · {overs} overs</>}
            </div>
          </div>
          <input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Your name (optional)" data-testid="guest-name" className="bg-transparent border border-white/20 py-3 px-4" />
          <input value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="Phone" data-testid="guest-phone" className="bg-transparent border border-white/20 py-3 px-4" />

          {/* Payment mode selector */}
          {partialAllowed && (
            <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-white/10 pt-5" data-testid="payment-mode-selector">
              <button
                type="button"
                onClick={() => setPaymentMode("partial")}
                data-testid="payment-mode-partial"
                className={`text-left p-4 border transition ${paymentMode === "partial" ? "border-brand-orange bg-brand-orange/10" : "border-white/12 hover:border-brand-blue"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">Pay {depositPct}% now · Balance at desk</div>
                  {paymentMode === "partial" && <span className="text-[10px] font-mono brand-orange">SELECTED</span>}
                </div>
                <div className="font-display text-2xl font-black mt-2">{formatINR(depositAmount)}</div>
                <div className="text-xs text-white/50 mt-1">Balance {formatINR(balanceDue)} payable at reception before check-in.</div>
              </button>
              <button
                type="button"
                onClick={() => setPaymentMode("full")}
                data-testid="payment-mode-full"
                className={`text-left p-4 border transition ${paymentMode === "full" ? "border-brand-blue bg-brand-blue/10" : "border-white/12 hover:border-brand-blue"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-blue">Pay 100% now</div>
                  {paymentMode === "full" && <span className="text-[10px] font-mono brand-blue">SELECTED</span>}
                </div>
                <div className="font-display text-2xl font-black mt-2">{formatINR(totalPrice)}</div>
                <div className="text-xs text-white/50 mt-1">No balance at reception. Walk in, scan & play.</div>
              </button>
            </div>
          )}

          <div className="md:col-span-3 flex items-center justify-between border-t border-white/10 pt-4 flex-wrap gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Total</div>
              <div className="font-display text-3xl brand-blue font-black">{formatINR(totalPrice)}</div>
              <div className="text-[10px] text-white/40 font-mono mt-0.5">
                {gstRate > 0
                  ? (gstInclusive
                      ? <>Incl. GST {gstRate}% ({formatINR(gstAmount)}) · Base {formatINR(baseExGst)}</>
                      : <>Subtotal {formatINR(subtotal)} · GST {gstRate}% {formatINR(gstAmount)}</>)
                  : <>GST included</>}
              </div>
              {partialAllowed && paymentMode === "partial" && (
                <div className="text-[11px] font-mono brand-orange mt-1" data-testid="balance-due-hint">
                  Pay now {formatINR(depositAmount)} · Balance at desk {formatINR(balanceDue)}
                </div>
              )}
            </div>
            <button
              onClick={submit}
              disabled={loading}
              data-testid="confirm-booking"
              className="bg-brand-blue text-white px-8 py-4 font-bold text-sm uppercase tracking-widest hover:glow-blue disabled:opacity-50"
            >
              {loading ? "Processing…" : `Pay & Confirm ${formatINR(payNow)}`}
            </button>
          </div>
        </div>
      )}

      {confirmation && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-5" onClick={() => setConfirmation(null)}>
          <div className="bg-[#131A2B] border border-brand-blue p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="confirmation-dialog">
            <CheckCircle2 className="text-brand-blue mx-auto" size={48} />
            <h3 className="font-display text-2xl font-black uppercase text-center mt-4">Booked!</h3>
            <div className="mt-5 flex justify-center">
              <div className="bg-white p-3 inline-block" data-testid="booking-qr">
                <QRCodeSVG value={confirmation.qr_token || ""} size={168} level="M" />
              </div>
            </div>
            <div className="mt-4 text-center text-[11px] font-mono uppercase tracking-widest text-white/60">
              Show this QR at reception
            </div>
            <div className="mt-4 font-mono text-xs text-white/60 space-y-1 text-center">
              <div>QR token: <code className="brand-blue break-all">{confirmation.qr_token}</code></div>
              <div>Total: {formatINR(confirmation.total)}</div>
              <div>Paid now: <span className="brand-blue">{formatINR(confirmation.amount_paid ?? confirmation.total)}</span></div>
              {(confirmation.balance_due || 0) > 0 && (
                <div className="brand-orange" data-testid="confirmation-balance-due">
                  Balance at desk: {formatINR(confirmation.balance_due)}
                </div>
              )}
              <div>Status: {confirmation.payment_status || confirmation.status}</div>
            </div>
            {confirmation.balance_payment_link_url && (
              <a
                href={confirmation.balance_payment_link_url}
                target="_blank" rel="noreferrer"
                data-testid="confirmation-balance-link"
                className="mt-4 block w-full text-center py-3 border border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-black font-mono text-[11px] uppercase tracking-widest"
              >
                Pay Balance Online (Razorpay)
              </a>
            )}
            <button onClick={() => setConfirmation(null)} className="mt-6 w-full py-3 border border-white/20 hover:border-brand-blue text-sm font-mono uppercase tracking-widest">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
